import React, { useState } from 'react';
import MyDataTable from '../../components/MyDataTable';
import { Pagination } from '../../components/Pagination';
import { translate } from '../../services/translate';
import { SignaturesParamList } from '../../typings/Params';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { ClientError, GenericPaginatedParameters, NotFound } from '../../services/central-api/base-service';
import { signaturesService } from '../../services/central-api/signatures';
import moment from 'moment';
import DeleteModal from '../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { AlreadySignedReport } from '../../services/central-api/dispatch';
import ConfirmIdentityModal from '../../components/ConfirmIdentityModal';
import Container from '../../components/Container';
import Header from '../../components/Header';
import Card from '../../components/Card';
import DataTableHeader from '../../components/DataTableHeader';
import DataTableContainer from '../../components/DataTableContainer';
import DropdownButton from '../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import { openExternalLinkInNewTab } from '../../services/navigation-assistant';

export default function Signatures() {
    const route = useRoute<RouteProp<SignaturesParamList, 'Signatures'>>();
    const navigation = useNavigation<NavigationProp<SignaturesParamList>>();

    const [confirmSignModalVisible, setConfirmSignModalVisible] = useState(false);
    const [selectedSignableReport, setSelectedSignableReport] = useState<SignableReport>();
    const [isConfirmIdentityModalVisible, setIsConfirmIdentityModalVisible] = useState(false);

    const [filters, setFilters] = useState<GenericPaginatedParameters>({
        page: Math.max(route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });

    const isFocused = useIsFocused();
    const { data, isLoading, refetch } = useQuery({
        queryKey: ['getPaginatedSupervisorPendingSignatures', filters],
        queryFn: () => signaturesService.getPaginatedSupervisorPendingSignatures(filters),
        enabled: isFocused,
        refetchInterval: isFocused ? 30000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
    });

    async function supervisorSignReport(signableReportId: number, params: { password: string; }) {
        try {
            await signaturesService.supervisorSignReport(signableReportId, params);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            refetch();
            return 'success';

        } catch (err) {
            if (err instanceof AlreadySignedReport) {
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message, { userName: err.userName }),
                });
                return 'failure';

            } else if (err instanceof NotFound) {
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
                return 'failure';
            } else if (err instanceof ClientError) {
                if (err.message === 'InvalidValue') {
                    Toast.show({
                        type: 'sentinelxError',
                        text1: translate('invalidPassword'),
                    });
                    return 'failure';
                }
                Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
                return 'failure';
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            return 'failure';

        }
    }

    async function openReportUrl(signableReport: SignableReport) {
        try {
            const res = await signaturesService.getSignableReportResultUrl(signableReport.id);
            openExternalLinkInNewTab(res.resultUrl);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }

    }

    return <Container>
        <Header title={translate('signatures')} />
        <Card>
            <DataTableHeader
                title={translate('pendingSignableReports')}
                zIndex={2}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('nProtocol'),
                        selector: row => row.occurrenceId,
                    },
                    {
                        name: translate('status'),
                        selector: row => translate(row.status),
                    },
                    {
                        name: translate('signedOn'),
                        selector: row => moment(row.teamSignedOn || '').format('DD/MM/YYYY HH:mm:ss'),
                    },
                    {
                        name: translate('signedBy'),
                        selector: row => `${row.teamSignedByUser?.warName} (RF ${row.teamSignedByUser?.registry})`,
                        grow: 3,
                    },
                    {
                        name: translate('unit'),
                        selector: row => `${row.actingBodyUnit.actingBody.name}/${row.actingBodyUnit.name}`,
                    },
                    {
                        name: translate('actions'),
                        button: true,
                        cell: row => <DropdownButton
                            actions={[{
                                content: translate('viewReport'),
                                onPress: () => openReportUrl(row),
                            }, {
                                content: translate('signReport'),
                                onPress: () => {
                                    setSelectedSignableReport(row);
                                    setConfirmSignModalVisible(true);
                                },
                            }]}
                        />,
                        wrap: true,
                    }
                    ]}
                    data={data?.rows ?? []}
                    progressPending={isLoading}
                    noResultsText={translate('noReportWithPendingSignature')}
                />}
                pagination={<Pagination
                    totalItems={data?.count ?? 0}
                    currentPage={filters.page}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                />}
            />
        </Card>

        <DeleteModal
            setModalVisible={setConfirmSignModalVisible}
            isModalVisible={confirmSignModalVisible}
            itemName={translate('object')}
            customConfirm={translate('sign')}
            customMessage={translate('confirmSignature', { occurrenceId: selectedSignableReport?.occurrenceId })}
            onSubmitType='primary'
            onSubmit={async () => {
                setIsConfirmIdentityModalVisible(true);
            }}
        />
        <ConfirmIdentityModal
            isModalVisible={isConfirmIdentityModalVisible}
            setModalVisible={setIsConfirmIdentityModalVisible}
            onSubmit={async (password) => {
                if (!selectedSignableReport) {
                    return 'success';
                }
                return await supervisorSignReport(selectedSignableReport.id, { password });
            }}

        />
    </Container>;
}
